import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import repositories from '../../repositories.jsx';
import _ from 'lodash';
import PageLoader from '../../components/PageLoader.jsx';
import Header from '../../components/Header.jsx';
import {useNavigate, useParams} from 'react-router-dom';
import {
    Button,
    Divider,
    Spinner,
    Tab,
    Tabs,
    Tooltip,
    useDisclosure,
} from "@heroui/react";
import PrimaryCard from '../../components/PrimaryCard.jsx';
import VideoScriptCard from '../../widgets/VideoScriptCard.jsx';
import {useForm} from '@mantine/form';
import VoiceSettings from '../../components/VoiceSettings.jsx';
import toast from 'react-hot-toast';
import {useGlobalAudioPlayer} from 'react-use-audio-player';
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {timestampToSecondsMs} from '../../helpers/AppHelper.jsx';
import VideoRenderer from "../../remotion/VideoRenderer.jsx";
import {Player} from "@remotion/player";
import PlayPauseButton from "../../widgets/player/PlayPauseButton.jsx";
import TimeDisplay from "../../widgets/player/TimeDisplay.jsx";
import SeekBar from "../../widgets/player/SeekBar.jsx";
import TimelineEditor from "../../widgets/TimelineEditor.jsx";
import ExportVideo from '../../components/ExportVideo.jsx';
import {useRecoilState} from 'recoil';
import {speakersState} from '../../store.jsx';
import {useMounted, useViewportSize} from '@mantine/hooks';
import {getVideoMetadata} from "@remotion/media-utils";
import Error404 from '../Error404.jsx';
import ConfirmDeductionModal from '../../components/ConfirmDeductionModal.jsx';
import {useVirtualizer} from '@tanstack/react-virtual';
import {AnimatePresence, motion, useUnmountEffect} from 'framer-motion';
import LeavePageModal from "../../widgets/LeavePageModal.jsx";
import FindAndReplaceModal from "../../components/FindAndReplaceModal.jsx";
import BufferCreditWarningModal from "../../components/BufferCreditWarningModal.jsx";
import {v4 as uuidv4} from 'uuid';


const generateId = () => {
    return uuidv4()
};

export default function ProjectEdit() {
    const {ID} = useParams();
    const navigate = useNavigate();
    const isMounted = useMounted();
    const {width, height} = useViewportSize()
    const [selectedSegmentId, setSelectedSegmentId] = useState(null);
    const [videoPlayerTab, setVideoPlayerTab] = useState('translated');
    const [speakers, setSpeakers] = useRecoilState(speakersState);
    const [isShowCreditModal, setIsShowCreditModal] = useState(false);
    const queryClient = useQueryClient();
    const [segmentData, setSegmentData] = useState([])
    const [isBuffering, setIsBuffering] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const segmentContainerRef = useRef(null);
    const videoPlayerContainer = useRef(null);
    const videoPlayer = useRef(null);
    const [fps] = useState(60);
    const [audioFiles, setAudioFiles] = useState([]);
    const [videoDuration, setVideoDuration] = useState(0);
    const [isGeneratingVideo, setIsGeneratingVideo] = useState(30000)
    const [currentVideoUrl, setCurrentVideoUrl] = useState('')
    const [buyCredits, setBuyCredits] = useState(null)
    const [currentTimelinePosition, setCurrentTimelinePosition] = useState(0);
    const {isOpen: findAndReplaceModalIsOpen, onOpenChange: findAndReplaceModalOnOpenChange} = useDisclosure()
    const {isOpen: bufferCreditUseModalIsOpen, onOpenChange: bufferCreditUseModalOnOpenChange} = useDisclosure()

    const projectForm = useForm({
        initialValues: {
            text_srt_json: [],
            isChanged: false,
            is_caption_enabled: false,
            is_change_available: false,
            is_main_credit_used: false,
            is_fit_translated_audio: false,
            is_timeline_changed: false,
        },
        mode: 'controlled'
    });
    const latestProjectValuesRef = useRef(projectForm.values);
    const audioFilesRef = useRef([]);

    const currentProject = useQuery({
        queryKey: ['get-projects', ID],
        queryFn: () => repositories.project.showProject(ID).then(async (res) => res).catch(error => {
            toast.error('Invalid project, please check and retry.')
            setIsGeneratingVideo(false)
            navigate('/app')
        }),
        refetchInterval: isGeneratingVideo,
    })

    const countOfChanges = useMemo(() => {
        return _.size(_.filter(projectForm.values.text_srt_json, {is_audio_changed: true}))
    }, [projectForm.values.text_srt_json])

    const countOfTimeStampChanges = useMemo(() => {
        return _.size(_.filter(projectForm.values.text_srt_json, {has_timeline_changed: true}))
    }, [projectForm.values.text_srt_json])

    const hasUnsavedChanges = useMemo(() => {
        return (
          countOfChanges > 0 ||
          countOfTimeStampChanges > 0 ||
          projectForm.values.is_change_available
        );
    }, [projectForm.values, countOfChanges, countOfTimeStampChanges]);

    useEffect(() => {
        setIsGeneratingVideo((currentProject.data?.export_status == 'in_progress' || currentProject.data?.export_status == 'rendering') ? 30000 : false)

        const processedSegments = _.map(currentProject.data?.text_srt_json, (item) => {
            item.is_audio_changed = false
            item.is_text_changed = false
            item.has_timeline_changed = false
            item.is_original_text_changed = false
            item.prev_translated_text = item?.translated_text
            if(!item.id) item.id = generateId();
            return item;
        });

        projectForm.setValues({
            ...currentProject.data,
            text_srt_json: processedSegments
        });

        if (currentProject?.data?.export_status === "completed") {
            queryClient.refetchQueries({
                queryKey: ['auth']
            })
        }

    }, [currentProject.data]);


    const update = useMutation({
        mutationFn: () => repositories.project.updateSrt(projectForm?.values),
        onSuccess: (data) => {
            queryClient.refetchQueries({
                queryKey: ['auth']
            })
            let currentPosition = 0;
            if (videoPlayer?.current) {
                const currentFrame = videoPlayer.current.getCurrentFrame();
                currentPosition = currentFrame / fps;
                setCurrentTimelinePosition(currentPosition);
            }

            if (bufferCreditUseModalIsOpen) {
                bufferCreditUseModalOnOpenChange()
            }

            setTimeout(() => {
                projectForm.setFieldValue('isChanged', false)
                projectForm.setFieldValue('is_change_available', false)
                projectForm.setFieldValue('prev_speakers', projectForm.values?.speakers)
                // Restore position
                if (videoPlayer?.current) {
                    videoPlayer.current.seekTo(currentPosition * fps);
                }

            }, 100)
        },
        onError: (error) => {
            if (error.response.data?.error_type == 'confirmation') {
                setIsShowCreditModal(true)
            } else {
                toast.error(error?.response?.data?.detail || 'Something went wrong')
            }
        }
    })

    useQuery({
        queryKey: ['get-project-speakers'],
        queryFn: () => repositories.speakers({translate_language: currentProject?.data?.translate_language || currentProject?.data?.original_language}).then((res) => {
            setSpeakers(res)
            return res;
        }),
        enabled: currentProject.isSuccess
    })

    const fetchAudios = useCallback(() => {
        const videoURL = currentProject.data?.s3_video_link || '';
        setCurrentVideoUrl(videoURL);

        // Save current position before making any changes
        let currentPosition = 0;
        if (videoPlayer?.current) {
            const currentFrame = videoPlayer.current.getCurrentFrame();
            currentPosition = currentFrame / fps;
        }

        const textSrtJson = currentProject.data?.text_srt_json || [];

        const _audioFiles = textSrtJson.map((item, index) => {
            const startTime = timestampToSecondsMs(item?.timestamp?.start) + 0.05;
            const endTime = timestampToSecondsMs(item?.timestamp?.end);
            const duration = timestampToSecondsMs(item?.audio_duration);
            const nextSegmentStart = item?.next_segment_start ?
              timestampToSecondsMs(item?.next_segment_start) : endTime;

            return {
                id: item.id || generateId(),
                url: item?.voice,
                start: startTime,
                end: endTime,
                duration,
                translated: item?.translated_text?.trim(),
                original: item?.original_text?.trim(),
                speaker_user: item?.speaker_user,
                next_segment_start: nextSegmentStart,
                originalStart: startTime,
                originalEnd: endTime,
                index: item?.index || index
            };
        });

        // Batch state updates
        setAudioFiles(_audioFiles);
        setSegmentData(_audioFiles);

        // Restore position after a short delay
        if (currentPosition > 0 && videoPlayer?.current) {
            requestAnimationFrame(() => {
                try {
                    videoPlayer.current.seekTo(currentPosition * fps);
                } catch (e) {
                    console.warn('Could not seek to position:', e);
                }
            });
        }
    }, [currentProject.data?.s3_video_link, currentProject.data?.text_srt_json, fps]);

    useLayoutEffect(() => {
        if (currentProject.data?.s3_video_link &&
          currentProject.data?.text_srt_json) {
            fetchAudios();
        }
    }, [currentProject.data?.s3_video_link, currentProject.data?.text_srt_json?.length]);


    useEffect(() => {
        if (!videoPlayer.current) return;

        const player = videoPlayer.current;
        const eventHandlers = {
            'play': () => setIsPlaying(true),
            'pause': () => setIsPlaying(false),
            'waiting': () => setIsBuffering(true),
            'resume': () => setIsBuffering(false)
        };

        // Add all listeners at once
        Object.entries(eventHandlers).forEach(([event, handler]) => {
            player.addEventListener(event, handler);
        });

        // Cleanup function removes all listeners
        return () => {
            if (player) {
                Object.entries(eventHandlers).forEach(([event, handler]) => {
                    player.removeEventListener(event, handler);
                });
            }
        };
    }, [videoPlayer]);


    const segmentRootRef = useRef(null)

    const rowVisualizer = useVirtualizer({
        count: _.size(projectForm.values?.text_srt_json || []),
        getScrollElement: () => segmentRootRef.current,
        estimateSize: () => 110,
        gap: 10,
        overscan: 5,
        getItemKey: (index) => {
            const item = projectForm.values?.text_srt_json?.[index];
            return item ? item.id : index;
        }
    })

    const [isLoading, setIsLoading] = useState(true)

    const getMediaData = useQuery({
        queryKey: ['getMediadata', currentVideoUrl],
        enabled: !_.isEmpty(currentVideoUrl),
        queryFn: (payload) => getVideoMetadata(currentVideoUrl).then((response) => {
            setVideoDuration(_.round(response.durationInSeconds || 1, 2))
            setIsLoading(false)
            return response;
        }),
    })

    const globalAudioPlayer = useGlobalAudioPlayer()

    useUnmountEffect(() => {
        globalAudioPlayer.stop()
    }, [])

    const onBuyCredits = useMutation({
        mutationFn: () => repositories.project.buyCredits({
            project_id: projectForm.values?.project_id,
            quota: buyCredits,
        }),
        onSuccess: data => {
            console.log(data)
            projectForm.setFieldValue('editing_quota', projectForm.values?.editing_quota + data?.editing_quota * 60)
            toast.success(data?.msg || 'Credits add successfully')
            setBuyCredits(null)
            queryClient.refetchQueries({
                queryKey: ['auth']
            })
        },
        onError: error => {
            toast.error(error?.response?.data?.detail || 'Something went wrong')
        }
    })

    const getChangedTextLength = (formData) => {
        const textSrtJson = formData.values.text_srt_json || {};
        let totalLength = 0;
        Object?.keys(textSrtJson)?.forEach(segmentKey => {
            const segment = textSrtJson[segmentKey];

            if (segment?.is_audio_changed === true) {
                const translatedText = segment?.translated_text || "";
                totalLength += translatedText?.length;
            }
        });
        return totalLength
    };

    useEffect(() => {
        latestProjectValuesRef.current = projectForm?.values;
    }, [projectForm?.values]);


    const consumedCredit = projectForm.values?.tts_charge * getChangedTextLength(projectForm);
    const isDisabled =
      consumedCredit > projectForm?.values?.editing_quota ||
      projectForm.values?.editing_quota === 0 ||
      !hasUnsavedChanges;

    useEffect(() => {
        const player = videoPlayer.current;
        if (!player) return;

        const handleKeyDown = (event) => {
            if (event.code === 'Space' &&
              !['INPUT', 'TEXTAREA'].includes(event.target.tagName)) {
                event.preventDefault();
                player.toggle();
            }
        };
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [videoPlayer.current]);

    useEffect(() => {
        audioFilesRef.current = audioFiles;
    }, [audioFiles]);

    return (
      <>
          <Header
            className="border-b border-white"
            leftArea={<div className="flex items-center">
                <LeavePageModal hasChanges={hasUnsavedChanges}/>

                <Divider orientation="vertical" className="h-6 w-0.5"/>
                <h3 className="ml-3 text-sm lg:text-xl font-sans truncate max-w-[60px] md:max-w-xs">{projectForm.values?.project_name}</h3>
            </div>}
          />

          <ConfirmDeductionModal
            isOpen={isShowCreditModal}
            onClosed={() => setIsShowCreditModal(false)}
            onConfirm={() => {
                projectForm.setFieldValue('is_main_credit_used', true)
                setTimeout(() => {
                    update.mutateAsync().then(() => {
                        currentProject.refetch()
                        queryClient.refetchQueries({
                            queryKey: ['auth']
                        })
                    })
                }, 300)
                setIsShowCreditModal(false)
            }}
          />

          <div className="relative hidden lg:block">
              {(isLoading || currentProject.isLoading) ?
                <div className="w-full h-full fixed left-0 top-0 transition-all duration-300">
                    <PageLoader/>
                </div>
                :
                <AnimatePresence>
                    {currentProject.isSuccess ?
                      <motion.div
                        key={1}
                        initial={{
                            opacity: 0
                        }}
                        animate={{
                            opacity: 1,
                            transition: {
                                duration: 1
                            }
                        }}
                        exit={{
                            opacity: 0
                        }}
                        className="grid grid-cols-12 gap-4 relative">
                          <div
                            ref={segmentContainerRef}
                            className="col-span-7 bg-white/40 border-2 rounded-20 border-white bg-no-repeat min-h-64 relative overflow-hidden">
                              {rowVisualizer.isScrolling &&
                                <div
                                  className="w-full h-full absolute left-0 top-0 bg-white/50 z-30  transition-all duration-300">
                                    <PageLoader/>
                                </div>
                              }

                              <div className="grid grid-cols-2 px-0.5 border-b border-white py-2 mb-2">
                                  <div className="px-6 flex items-center font-sans">
                                      <h2 className="capitalize">
                                          {projectForm.values?.original_language}
                                      </h2>
                                  </div>
                                  <div className="pl-4 pr-3 flex items-center justify-between w-full font-sans">
                                      <div className="capitalize">
                                          {projectForm.values?.translate_language}
                                      </div>
                                      <div className="flex justify-end items-center gap-3">
                                          <VoiceSettings
                                            form={projectForm}
                                            prevData={currentProject.data}
                                            onValueChange={projectForm.setFieldValue}
                                            fitAudio={projectForm.values?.is_fit_translated_audio}
                                            setFitAudio={(value) => {
                                                if(currentProject?.data?.is_fit_translated_audio !== value) {
                                                    projectForm.setFieldValue('is_change_available', true)
                                                }
                                                projectForm.setFieldValue('is_fit_translated_audio', value)
                                            }
                                          }
                                          />
                                      </div>
                                  </div>
                              </div>
                              <div
                                ref={segmentRootRef}
                                style={{
                                    height: `${height - 340}px`,
                                    minHeight: `${height - 340}px`,
                                    maxHeight: `${height - 340}px`,
                                }}
                                className="col-span-full overflow-y-auto">
                                  <div
                                    style={{
                                        height: `${rowVisualizer.getTotalSize()}px`
                                    }}
                                    className="relative"
                                  >
                                      {rowVisualizer.getVirtualItems()?.map((virtualRow) => {
                                          const index = virtualRow.index;
                                          const currentId = virtualRow.key;

                                          const segmentData = _.get(projectForm.values.text_srt_json, index) || {};
                                          const prevSegmentData = _.get(currentProject?.data?.text_srt_json, index);
                                          const isSelected = selectedSegmentId === currentId;

                                          return (
                                            <div
                                              key={currentId}
                                              data-index={index}
                                              ref={rowVisualizer.measureElement}
                                              style={{
                                                  height: `${virtualRow.size}px`,
                                                  maxHeight: `${virtualRow.size}px`,
                                                  transform: `translateY(${virtualRow.start}px)`,
                                              }}
                                              className="absolute left-0 top-0 w-full translate-y-1/2 translate-x-1/2"
                                            >
                                                <VideoScriptCard
                                                  key={currentId}
                                                  id={currentId}
                                                  fps={fps}
                                                  segmentKey={index}
                                                  ref={rowVisualizer.measureElement}
                                                  playerRef={videoPlayer}
                                                  segments={projectForm.values.text_srt_json} // Pass current form values
                                                  onSelected={() => {
                                                      setSelectedSegmentId(currentId)
                                                  }}
                                                  onChange={(key, value) => {
                                                      const actualIndex = projectForm.values.text_srt_json.findIndex(item => item.id === currentId);

                                                      if (actualIndex === -1) {
                                                          console.error("Segment ID not found:", currentId);
                                                          return;
                                                      }

                                                      const fieldPath = key ? `text_srt_json.${actualIndex}.${key}` : `text_srt_json.${actualIndex}`;
                                                      projectForm.setFieldValue(fieldPath, value);

                                                      if (!key) {
                                                          if(value?.current_buffer_credit) {
                                                              projectForm.setFieldValue('current_buffer_credit', value?.current_buffer_credit);
                                                          }

                                                          if (!value?.is_new_segment) {
                                                              let currentPos = 0;
                                                              if (videoPlayer?.current) {
                                                                  const currentFrame = videoPlayer.current.getCurrentFrame();
                                                                  currentPos = currentFrame / fps;
                                                              }

                                                              // Update Audio Files State
                                                              const updatedAudioFiles = [...audioFiles];
                                                              // Find audio file by ID
                                                              const audioIndex = updatedAudioFiles.findIndex(a => a.id === currentId);

                                                              if(audioIndex > -1) {
                                                                  const updatedSegment = {...updatedAudioFiles[audioIndex]};
                                                                  updatedSegment.url = value?.voice;
                                                                  updatedSegment.duration = timestampToSecondsMs(value?.audio_duration);
                                                                  updatedSegment.translated = value?.translated_text?.trim();
                                                                  updatedSegment.speaker_user = value?.speaker_user;

                                                                  updatedAudioFiles[audioIndex] = updatedSegment;
                                                                  setAudioFiles(updatedAudioFiles);
                                                                  setSegmentData([...updatedAudioFiles]);
                                                              }

                                                              setTimeout(() => {
                                                                  setCurrentTimelinePosition(currentPos);
                                                              }, 100);

                                                              toast.success('Segment updated successfully');
                                                          }
                                                          else
                                                          {
                                                              const start = timestampToSecondsMs(value.timestamp.start);
                                                              const end = timestampToSecondsMs(value.timestamp.end);

                                                              const segmentId = value?.id || generateId();

                                                              const newAudioSegment = {
                                                                  url: value.voice || '',
                                                                  id: segmentId,
                                                                  start,
                                                                  end,
                                                                  duration: timestampToSecondsMs(value.audio_duration),
                                                                  translated: value.translated_text?.trim(),
                                                                  original: value.original_text?.trim(),
                                                                  speaker_user: value.speaker_user,
                                                                  index: actualIndex,
                                                                  originalStart: start,
                                                                  originalEnd: end,
                                                              };

                                                              const updatedAudioFiles = [...audioFilesRef.current];

                                                              const existingIndex = updatedAudioFiles.findIndex(
                                                                seg => seg.id === segmentId
                                                              );

                                                              if (existingIndex !== -1) {
                                                                  updatedAudioFiles[existingIndex] = {
                                                                      ...updatedAudioFiles[existingIndex],
                                                                      ...newAudioSegment,
                                                                  };
                                                                  toast.success('New Segment added successfully');
                                                              } else {
                                                                  updatedAudioFiles.splice(actualIndex, 0, newAudioSegment);
                                                              }

                                                              const normalizedAudioFiles = updatedAudioFiles
                                                                .map((seg, idx) => ({ ...seg, index: idx }))
                                                                .sort((a, b) => a.index - b.index);

                                                              setAudioFiles(normalizedAudioFiles);
                                                              setSegmentData(normalizedAudioFiles);
                                                          }

                                                      }
                                                  }}
                                                  onRemoved={async () => {
                                                      await queryClient.cancelQueries({ queryKey: ['get-projects', ID] });
                                                      const currentSegments = projectForm.values.text_srt_json.filter(item => item.id !== currentId);
                                                      const currentAudioFiles = audioFiles.filter(item => item.id !== currentId);
                                                      projectForm.setFieldValue('text_srt_json', currentSegments);
                                                      setAudioFiles(currentAudioFiles);
                                                      setSegmentData(currentAudioFiles);
                                                      toast.success('Segment removed successfully');

                                                      if(selectedSegmentId === currentId) {
                                                          setSelectedSegmentId(null);
                                                      }
                                                  }}
                                                  isSelected={isSelected}
                                                  speakers={projectForm.values?.speakers || []}
                                                  prevData={prevSegmentData}
                                                  currentProject={update}
                                                  data={{
                                                      ...segmentData,
                                                      project_id: projectForm.values?.project_id,
                                                      translated_language: projectForm.values?.translate_language,
                                                  }}
                                                  formData={projectForm}
                                                  isLoading={update.isPending || isLoading}
                                                />
                                            </div>
                                          );
                                      })}
                                  </div>
                              </div>
                          </div>
                          <div
                            key={currentVideoUrl}
                            className="col-span-5 flex flex-col bg-gradient-to-b from-white/50 to-white/0 rounded-20 overflow-hiddens border-2 border-white min-h-64 relative overflow-hidden">
                              <div className="min-h-16 flex items-center justify-between px-1.5 pb-3 pt-1 gap-2">
                                  <Tooltip content="Apply Changes">
                                      <Button
                                        isDisabled={isDisabled}
                                        isLoading={update.isPending || isLoading}
                                        onPress={() => {
                                            if (projectForm.values?.current_buffer_credit && consumedCredit > projectForm.values?.current_buffer_credit) {
                                                bufferCreditUseModalOnOpenChange();
                                            } else {
                                                if (videoPlayer?.current?.isPlaying()) {
                                                    videoPlayer.current.pause();
                                                }

                                                update.mutateAsync().then((response) => {
                                                    if (response?.error_type == 'insufficient_quota') {
                                                        toast.error(response?.error || 'Insufficient quota. Please purchase additional credits or upgrade your plan')
                                                    } else {
                                                        currentProject.refetch();
                                                    }
                                                })
                                            }
                                        }} variant="bordered" color=""
                                        fullWidth
                                        className="py-5 px-2 text-sm border-divider font-sans"
                                      >
                                          <i className="hgi hgi-stroke font-medium text-[25px] hgi-card-exchange-01"></i> {countOfChanges > 0 && (
                                        <>
                                            ( {countOfChanges || 0} )&nbsp;
                                            <Tooltip
                                              content={`Total consumed: ${projectForm.values?.tts_charge * getChangedTextLength(projectForm)} credit`}>
                                                <div
                                                  className="min-w-7 min-h-7 h-7 w-7 bg-[#8C2CE2] rounded-full flex justify-center items-center">
                                                    <i className="hgi-stroke hgi-coins-01 text-white text-medium"></i>
                                                </div>
                                            </Tooltip>
                                        </>
                                      )}
                                      </Button>
                                  </Tooltip>
                                  <Tooltip content="Find & Replace">
                                      <Button
                                        isIconOnly
                                        variant="bordered" color=""
                                        className="py-5 px-2 text-sm border-divider font-sans"
                                        onPress={findAndReplaceModalOnOpenChange}
                                      >
                                          <img className="w-[25px] h-[20px]" src="/images/find_and_replace.png"
                                               alt="find_and_replace.png"/>
                                      </Button>
                                  </Tooltip>
                                  <Tabs
                                    color=""
                                    radius="md"
                                    selectedKey={videoPlayerTab}
                                    onSelectionChange={setVideoPlayerTab}
                                    classNames={{
                                        base: 'rounded-2xl bg-dark/5 sborder-2 border-white',
                                        tabList: 'w-fit',
                                        tab: 'px-2 py-5 w-full font-sans w-fit',
                                        tabContent: "group-data-[selected=true]:text-white",
                                        cursor: "bg-black text-white rounded-xl"
                                    }}
                                    aria-label="Tabs variants" variant="light">
                                      <Tab key="original" title="Original"/>
                                      <Tab key="translated" title="Translated"/>
                                  </Tabs>
                                  <ExportVideo data={projectForm.values} originalData={currentProject.data}
                                               onUpdate={update}/>
                              </div>
                              {currentProject.isFetched &&
                                <div
                                  ref={videoPlayerContainer}
                                  className="relative flex flex-col h-full">
                                    <div className="grow bg-black/60 relative">
                                        <img src={currentProject.data?.thumbnail} alt="video_poster"
                                             className="w-full h-full absolute blur"/>
                                        <Player
                                          ref={videoPlayer}
                                          fps={fps}
                                          initialFrame={0}
                                          doubleClickToFullscreen={true}
                                          spaceKeyToPlayOrPause={true}
                                          component={VideoRenderer}
                                          showPosterWhenBuffering={false}
                                          compositionWidth={getMediaData?.data?.width || 1920}
                                          compositionHeight={getMediaData?.data?.height || 1080}
                                          errorFallback={err => {
                                              console.log(err)
                                          }}
                                          style={{
                                              width: '100%',
                                              height: '100%',
                                              overflow: 'hidden',
                                              backfaceVisibility: 'hidden',
                                          }}
                                          numberOfSharedAudioTags={30}
                                          durationInFrames={Math.ceil((videoDuration || 1) * fps)}
                                          key={`player-${currentVideoUrl}`}
                                          allowFrameSkipping={true}
                                          moveToBeginningWhenEnded={false}
                                          clickToPlay={false}
                                          inputProps={{
                                              videoDuration: videoDuration || 1,
                                              originalVideo: currentVideoUrl,
                                              backgroundMusic: currentProject.data?.background_music_link,
                                              audioFiles: audioFiles,
                                              currentMode: videoPlayerTab,
                                              isCaptionEnabled: projectForm.values?.is_caption_enabled,
                                              isWatermarkEnabled: projectForm.values?.is_watermark_enable,
                                              fitAudio: projectForm.values?.is_fit_translated_audio
                                          }}
                                        />
                                    </div>

                                    <SeekBar
                                      playerRef={videoPlayer}
                                      fps={fps}
                                      segments={currentProject.data?.text_srt_json || []}
                                      scrollToPosition={(scrollIndex) => {
                                          const segment = projectForm.values.text_srt_json[scrollIndex];
                                          if(segment?.id) setSelectedSegmentId(segment.id);
                                          rowVisualizer.scrollToIndex(scrollIndex)
                                      }}
                                      durationInFrames={Math.max(parseInt(videoDuration * fps), 1)}/>
                                    <div
                                      className="flex justify-between items-center gap-2 py-3 px-3 flex-shrink-0 bg-white">
                                        <div className="flex items-center gap-4">
                                            <PlayPauseButton playerRef={videoPlayer}/>
                                            <TimeDisplay
                                              fps={fps}
                                              playerRef={videoPlayer}
                                              durationInFrames={Math.max(parseInt(videoDuration * fps), 1)}/>
                                        </div>
                                        <div className="flex items-center">
                                            <Button
                                              size="lg"
                                              radius="full"
                                              isIconOnly={true}
                                              variant={projectForm.values?.is_caption_enabled ? "solid" : "light"}
                                              onPress={() => {
                                                  if(currentProject?.data?.is_caption_enabled !== !projectForm.values?.is_caption_enabled) {
                                                      projectForm.setFieldValue('is_change_available', true)
                                                  }
                                                  projectForm.setFieldValue('is_caption_enabled', (prev) => !prev)
                                              }}>
                                                {projectForm.values?.is_caption_enabled
                                                  ? <i className="hgi-stroke hgi-open-caption text-2xl"></i>
                                                  : <i className="hgi-stroke hgi-closed-caption text-2xl"></i>
                                                }
                                            </Button>
                                            <Button
                                              size="lg"
                                              radius="full"
                                              isIconOnly={true}
                                              variant="light"
                                              onPress={() => {
                                                  if (videoPlayerContainer.current) {
                                                      if (!document.fullscreenElement) {
                                                          videoPlayerContainer.current.requestFullscreen();
                                                      } else {
                                                          document.exitFullscreen();
                                                      }
                                                  }
                                              }}>

                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                     className="w-6"
                                                     color="currentColor" fill="none">
                                                    <path
                                                      d="M15.5 21C16.8956 21 17.5933 21 18.1611 20.8278C19.4395 20.44 20.44 19.4395 20.8278 18.1611C21 17.5933 21 16.8956 21 15.5M21 8.5C21 7.10444 21 6.40666 20.8278 5.83886C20.44 4.56046 19.4395 3.56004 18.1611 3.17224C17.5933 3 16.8956 3 15.5 3M8.5 21C7.10444 21 6.40666 21 5.83886 20.8278C4.56046 20.44 3.56004 19.4395 3.17224 18.1611C3 17.5933 3 16.8956 3 15.5M3 8.5C3 7.10444 3 6.40666 3.17224 5.83886C3.56004 4.56046 4.56046 3.56004 5.83886 3.17224C6.40666 3 7.10444 3 8.5 3"
                                                      stroke="currentColor"
                                                      strokeWidth="1.5"
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"/>
                                                </svg>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                              }
                          </div>

                          {isMounted && <PrimaryCard
                            key={`time_line_${isMounted}`}
                            className="col-span-full bg-white text-center overflow-hidden px-2 pt-3 timelineEditor h-32">
                              <TimelineEditor
                                fps={fps}
                                playerRef={videoPlayer}
                                scrollToPosition={(scrollIndex) => {
                                    const segment = projectForm.values.text_srt_json[scrollIndex];
                                    if(segment?.id) setSelectedSegmentId(segment.id);
                                    rowVisualizer.scrollToIndex(scrollIndex)
                                }}
                                segmentData={segmentData}
                                key={currentVideoUrl}
                                segments={audioFiles || []}
                                currentPosition={currentTimelinePosition}
                                onRegionUpdated={(id, newStart, newEnd) => {
                                    const latestAudioFiles = audioFilesRef.current;
                                    const channelIdx = latestAudioFiles.findIndex(f => f.id === id);

                                    if (channelIdx === -1) {
                                        console.error(`Audio Segment not found for ID: ${id}`);
                                        return;
                                    }

                                    const GAP_LIMIT = 0.1;
                                    let validatedStart = newStart;
                                    let validatedEnd = newEnd;

                                    if (channelIdx > 0) {
                                        const prevSegment = latestAudioFiles[channelIdx - 1];
                                        const minAllowedStart = prevSegment.end + GAP_LIMIT;

                                        if (validatedStart < minAllowedStart) {
                                            validatedStart = minAllowedStart;
                                        }
                                    }

                                    if (channelIdx < latestAudioFiles.length - 1) {
                                        const nextSegment = latestAudioFiles[channelIdx + 1];
                                        const maxAllowedEnd = nextSegment.start - GAP_LIMIT;

                                        if (validatedEnd > maxAllowedEnd) {
                                            validatedEnd = maxAllowedEnd;
                                        }
                                    }

                                    if (validatedStart >= validatedEnd) {
                                        return;
                                    }

                                    const segmentAudio = latestAudioFiles[channelIdx];
                                    const targetId = segmentAudio.id;
                                    const latestValues = latestProjectValuesRef.current;
                                    const formIndex = latestValues.text_srt_json.findIndex(x => x.id === targetId);
                                    if (formIndex === -1) return;

                                    const currentTextSrtJson = [...latestValues.text_srt_json];

                                    const originalStart = segmentAudio.start;
                                    const originalEnd = segmentAudio.end;

                                    segmentAudio.start = validatedStart;
                                    segmentAudio.end = validatedEnd;

                                    const secondsToTimestamp = (seconds) => {
                                        const hours = Math.floor(seconds / 3600);
                                        const minutes = Math.floor((seconds % 3600) / 60);
                                        const secs = Math.floor(seconds % 60);
                                        const ms = Math.round((seconds % 1) * 1000);
                                        return `${hours.toString().padStart(2,'0')}:${minutes
                                          .toString().padStart(2,'0')}:${secs
                                          .toString().padStart(2,'0')},${ms.toString().padStart(3,'0')}`;
                                    };

                                    currentTextSrtJson[formIndex].timestamp.start = secondsToTimestamp(validatedStart);
                                    currentTextSrtJson[formIndex].timestamp.end = secondsToTimestamp(validatedEnd);
                                    currentTextSrtJson[formIndex].has_timeline_changed = true;

                                    if (formIndex > 0) {
                                        currentTextSrtJson[formIndex - 1].next_segment_start = secondsToTimestamp(validatedStart);
                                    }

                                    projectForm.setFieldValue('text_srt_json', currentTextSrtJson);

                                    const updatedAudioFiles = [...latestAudioFiles];
                                    updatedAudioFiles[channelIdx] = {
                                        ...segmentAudio,
                                        originalStart,
                                        originalEnd,
                                    };

                                    if (channelIdx > 0) {
                                        updatedAudioFiles[channelIdx - 1].next_segment_start = validatedStart;
                                    }

                                    setAudioFiles(updatedAudioFiles);
                                    projectForm.setFieldValue('is_timeline_changed', true);

                                    if (videoPlayer?.current?.isPlaying()) {
                                        videoPlayer.current.seekTo(Math.max(validatedStart, 0) * fps);
                                    }
                                }}
                                updateLoading={false}
                                audio={(currentProject.data?.s3_compressed_audio_url || currentVideoUrl || currentProject.data?.s3_original_audio_link)}
                              />
                          </PrimaryCard>}
                          {(projectForm.values?.export_status == 'in_progress' || projectForm.values?.export_status == 'rendering') && (
                            <div
                              className="absolute top-0 right-0 z-50 w-full h-full rounded-20 flex flex-col justify-center items-center bg-white/50 text-black backdrop-blur">
                                <Spinner size="xl" className="scale-125" color="primary"/>
                                <p className="mt-5">
                                    Your video is being processed. This may take up to 5 minutes — feel free to navigate away, we’ve got it covered.
                                </p>
                                <p className="mt-2">
                                    Rendering: {_.round(projectForm.values?.export_percentage, 2)}%
                                </p>
                            </div>
                          )}
                      </motion.div>
                      :
                      <Error404 className="w-full h-full min-h-[80dvh]"/>
                    }
                </AnimatePresence>

              }
          </div>
          <div className="lg:hidden w-full h-[80dvh] flex justify-center items-center text-center px-5">
              <h1>Video dub editor isn't available on mobile. Please use a computer.</h1>
          </div>

          <FindAndReplaceModal
            isOpen={findAndReplaceModalIsOpen}
            onOpenChange={findAndReplaceModalOnOpenChange}
            projectForm={projectForm}
            onConfirm={() => {

            }}
          />

          <BufferCreditWarningModal
            isOpen={bufferCreditUseModalIsOpen}
            onOpenChange={bufferCreditUseModalOnOpenChange}
            isLoading={update.isPending}
            onConfirm={() => {
                if (videoPlayer?.current?.isPlaying()) {
                    videoPlayer.current.pause();
                }
                update.mutateAsync().then((response) => {
                    if (response?.error_type == 'insufficient_quota') {
                        toast.error(response?.error || 'Insufficient quota. Please purchase additional credits or upgrade your plan')
                    } else {
                        currentProject.refetch();
                    }
                })
            }}
          />

      </>
    );
}